import {
  AGENT_LOGIN_PATH,
  AGENT_LOGOUT_PATH,
  AGENT_PROFILE_PATH,
  AGENT_CHANGE_PASSWORD_PATH,
  AGENT_REGISTER_PATH,
  AGENT_REGISTER_OTP_SEND_PATH,
  AGENT_REGISTER_OTP_VERIFY_PATH,
  AGENT_REGISTER_OTP_RESEND_PATH,
  AGENT_ONBOARDING_PATH,
  AGENT_ONBOARDING_SUBMIT_PATH,
  AGENT_VERIFY_EMAIL_PATH,
  AGENT_RESEND_VERIFICATION_PATH,
  AGENT_FORGOT_PASSWORD_PATH,
  AGENT_FORGOT_PASSWORD_OTP_VERIFY_PATH,
  AGENT_RESET_PASSWORD_PATH,
} from './agentConfig';
import { persistSuccessAction } from './alerts';
import { agentApi } from './apiClient';
import { getErrorMessage } from './apiMessages';
import { getAgentHomePath } from './agentOnboarding';
import {
  clearAgentProfileCache,
  clearAgentToken,
  getAgentProfileCache,
  getAgentToken,
  setAgentProfileCache,
  setAgentToken,
} from './tokenStore';

function extractAuthPayload(payload) {
  return payload?.data || payload || {};
}

function readAgentTokenClaims(token = getAgentToken()) {
  if (!token) return null;
  const segment = String(token).split(".")[1];
  if (!segment) return null;
  try {
    const padded = segment.replace(/-/g, "+").replace(/_/g, "/");
    const decode = typeof atob === "function" ? atob : null;
    if (!decode) return null;
    return JSON.parse(decode(padded));
  } catch (_error) {
    return null;
  }
}

function applyLoggedInIdentity(profile, claims = readAgentTokenClaims()) {
  if (!profile) return profile;
  const agencyUserId = claims?.agencyUserId || claims?.agency_user_id || profile.agency_user_id || null;
  const agencyRole = claims?.agencyRole || claims?.agency_role || profile.agency_role || profile.agencyRole || "agency_admin";
  const next = {
    ...profile,
    agency_role: agencyRole,
    agencyRole,
    agency_user_id: agencyUserId,
    agency_name: profile.agency_name || profile.branch_name,
  };
  if (!agencyUserId || !claims?.email) {
    return next;
  }

  const ownerEmail = profile.owner_email || "";
  const claimIsOwner = ownerEmail && claims.email === ownerEmail;
  if (!claimIsOwner && next.email !== claims.email) {
    next.full_name = claims.name || next.full_name;
    next.email = claims.email;
    next.mobile = claims.mobile || next.mobile;
    next.utp_full_name = next.full_name;
    next.utp_email = next.email;
  }

  return next;
}

export async function loginAgent({ email, password }) {
  const payload = await agentApi.post(AGENT_LOGIN_PATH, {
    authToken: null,
    body: {
      utp_email: email,
      utp_system_pswd: password,
    },
  });

  const data = extractAuthPayload(payload);
  const token = data.token;

  if (!token) {
    throw new Error(getErrorMessage('LOGIN_TOKEN_MISSING'));
  }

  setAgentToken(token);
  if (data.partner) {
    data.partner.access_level = data.partner.access_level || data.partner.accessLevel;
    data.partner.verification_status = data.partner.verification_status || data.partner.verificationStatus;
  }
  return data;
}

export async function sendAgentRegistrationOtp({ email, fullName, otpToken }) {
  const response = await agentApi.post(AGENT_REGISTER_OTP_SEND_PATH, {
    authToken: null,
    body: { email, fullName, otpToken },
  });
  return extractAuthPayload(response);
}

export async function verifyAgentRegistrationOtp({ email, otp, otpToken }) {
  const response = await agentApi.post(AGENT_REGISTER_OTP_VERIFY_PATH, {
    authToken: null,
    body: { email, otp, otpToken },
  });
  return extractAuthPayload(response);
}

export async function resendAgentRegistrationOtp({ email, fullName, otpToken }) {
  const response = await agentApi.post(AGENT_REGISTER_OTP_RESEND_PATH, {
    authToken: null,
    body: { email, fullName, otpToken },
  });
  return extractAuthPayload(response);
}

export async function registerAgent(payload) {
  const response = await agentApi.post(AGENT_REGISTER_PATH, {
    authToken: null,
    body: payload,
  });
  const data = extractAuthPayload(response);
  if (data?.token) {
    setAgentToken(data.token);
  }
  return data;
}

export async function fetchAgentOnboarding() {
  const payload = await agentApi.get(AGENT_ONBOARDING_PATH);
  return extractAuthPayload(payload);
}

export async function submitAgentOnboarding(body) {
  const payload = await agentApi.post(AGENT_ONBOARDING_SUBMIT_PATH, { body });
  const data = extractAuthPayload(payload);
  const token = getAgentToken();
  if (token && data?.profile) {
    setAgentProfileCache(token, data.profile);
  }
  return data;
}

export function resolveAgentHomePath(profile) {
  return getAgentHomePath(profile);
}

export async function verifyAgentEmail({ email, token }) {
  const response = await agentApi.post(AGENT_VERIFY_EMAIL_PATH, {
    authToken: null,
    body: { email, token },
  });

  return extractAuthPayload(response);
}

export async function resendAgentVerification(email) {
  const response = await agentApi.post(AGENT_RESEND_VERIFICATION_PATH, {
    authToken: null,
    body: { email },
  });

  return extractAuthPayload(response);
}

export async function requestAgentPasswordReset(email, { otpToken } = {}) {
  const response = await agentApi.post(AGENT_FORGOT_PASSWORD_PATH, {
    authToken: null,
    body: { email, otpToken },
  });

  return extractAuthPayload(response);
}

export async function verifyAgentPasswordResetOtp({ email, otp, otpToken }) {
  const response = await agentApi.post(AGENT_FORGOT_PASSWORD_OTP_VERIFY_PATH, {
    authToken: null,
    body: { email, otp, otpToken },
  });

  const data = extractAuthPayload(response);
  const resetToken = data.resetToken || data.reset_token || '';
  if (resetToken && typeof window !== 'undefined') {
    window.sessionStorage.setItem('way2umrah.agent.resetToken', resetToken);
  }
  return { ...data, resetToken };
}

export async function resetAgentPassword({ token, resetToken, newPassword, confirmPassword }) {
  const resolvedToken =
    resetToken ||
    token ||
    (typeof window !== 'undefined' ? window.sessionStorage.getItem('way2umrah.agent.resetToken') : '');
  const response = await agentApi.post(AGENT_RESET_PASSWORD_PATH, {
    authToken: null,
    body: {
      token: resolvedToken,
      resetToken: resolvedToken,
      newPassword,
      confirmPassword,
    },
  });

  if (typeof window !== 'undefined') {
    window.sessionStorage.removeItem('way2umrah.agent.resetToken');
  }
  return extractAuthPayload(response);
}

function isUnauthorized(error) {
  return error?.status === 401;
}

export async function fetchCurrentAgent({ force = false } = {}) {
  const token = getAgentToken();
  if (!token) {
    throw new Error('Not signed in');
  }

  const cached = getAgentProfileCache(token);
  if (cached.promise) {
    return cached.promise;
  }
  if (!force && cached.profile) {
    return applyLoggedInIdentity(cached.profile);
  }

  const request = (async () => {
    try {
      const payload = await agentApi.get(AGENT_PROFILE_PATH);
      const data = extractAuthPayload(payload);
      const profile = applyLoggedInIdentity(data.profile || data.partner || data);
      setAgentProfileCache(token, profile);
      return profile;
    } catch (error) {
      if (getAgentProfileCache(token).promise === request) {
        setAgentProfileCache(token, cached.profile, null);
      }
      if (cached.profile && !isUnauthorized(error)) {
        return cached.profile;
      }
      throw error;
    }
  })();

  setAgentProfileCache(token, cached.profile, request);

  try {
    return await request;
  } catch (error) {
    if (getAgentProfileCache(token).promise === request) {
      setAgentProfileCache(token, cached.profile, null);
    }
    throw error;
  }
}

const PROFILE_FIELD_MAP = {
  full_name: 'utp_full_name',
  designation: 'utp_designation',
  mobile: 'utp_mobile',
  branch_name: 'utp_branch_name',
  agency_phone: 'utp_agency_phone',
  address_line1: 'utp_addr_one',
  address_line2: 'utp_addr_two',
  postal_code: 'utp_postal_code',
  fax_number: 'utp_fax_no',
  business_type: 'utp_buisness_type',
  office_space: 'utp_office_space',
  billing_address: 'utp_billing_address',
  billing_contact_person: 'utp_billing_contact_person',
  billing_email: 'utp_billing_email',
  billing_mobile: 'utp_billing_mobile',
  bank_details: 'utp_bank_details',
  payment_remarks: 'utp_payment_remarks',
  country_id: 'fk_country_id',
  state_id: 'fk_state_id',
  city_id: 'fk_city_id',
};

export async function updateCurrentAgent(profile) {
  const body = {};

  Object.entries(PROFILE_FIELD_MAP).forEach(([alias, column]) => {
    if (profile[column] !== undefined) {
      body[column] = profile[column];
    } else if (profile[alias] !== undefined) {
      body[column] = profile[alias];
    }
  });

  ['fk_country_id', 'fk_state_id', 'fk_city_id'].forEach((column) => {
    if (body[column] === undefined) {
      return;
    }
    const id = Number.parseInt(body[column], 10);
    body[column] = Number.isFinite(id) && id > 0 ? id : 0;
  });

  const payload = await agentApi.put(AGENT_PROFILE_PATH, { body });
  const data = extractAuthPayload(payload);
  const updated = applyLoggedInIdentity(data.updatedProfile || data.profile || data);
  const token = getAgentToken();
  if (token && updated) {
    setAgentProfileCache(token, updated);
  }
  return updated;
}

export async function changeAgentPassword({ currentPassword, newPassword, confirmPassword }) {
  const payload = await agentApi.put(AGENT_CHANGE_PASSWORD_PATH, {
    body: { currentPassword, newPassword, confirmPassword },
  });
  return extractAuthPayload(payload);
}

export async function logoutAgent() {
  try {
    await agentApi.post(AGENT_LOGOUT_PATH, {
      authToken: undefined,
      body: {},
    });
  } finally {
    clearAgentProfileCache();
    clearAgentToken();
  }
}

export async function logoutAgentAndRedirect() {
  await logoutAgent();
  persistSuccessAction("LOGOUT");
  window.location.replace("/login");
}

export function hasAgentToken() {
  return !!(typeof window !== 'undefined' && window.sessionStorage.getItem('way2umrah.agent.accessToken'));
}
